import { jsx, jsxs } from "react/jsx-runtime";
import { c } from "react/compiler-runtime";
import { useState } from "react";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import MuiTable from "@mui/material/Table";
import TableContainer$1 from "@mui/material/TableContainer";
import MuiTableHead from "@mui/material/TableHead";
import TableBody$1 from "./Body.js";
import TableHeader from "./Header.js";
import DynamicTableSkeleton from "./Skeleton.js";
import TableBody from "./Summary.js";
const TableContainer = styled(TableContainer$1, {
  shouldForwardProp: prop => !["externalHeader", "maxHeight"].includes(prop)
})(({
  theme,
  externalHeader,
  maxHeight
}) => ({
  maxHeight,
  // height: '100%',
  /**
   * Scrollbar styling
   */
  scrollbarWidth: "thin",
  scrollbarColor: `${theme.colors.border.tertiary} transparent`,
  "&::-webkit-scrollbar": {
    display: "block",
    width: "0.375rem",
    height: "0.375rem",
    backgroundColor: theme.colors.border.primary
  },
  "&::-webkit-scrollbar-track": {
    // background: theme.colors.surface.brand,
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: theme.sizes.radius.full
    // backgroundColor: theme.colors.border.hover,
  },
  /**
   * Table cell styles
   * These styles are applied to all table cells.
   * They include typography, background color, border, padding, and alignment.
   * The styles are applied to the root cell class.
   */
  [`& .${tableCellClasses.body}`]: {
    padding: `${theme.sizes.spacing["05"]} ${theme.sizes.spacing["07"]}`,
    ...theme.typography.h6,
    color: theme.colors.text.secondary,
    borderBottom: `${theme.sizes.borderWidth["1"]} solid ${theme.colors.border.primary}`,
    "&.text": {
      textAlign: "left"
    },
    "&.value": {
      textAlign: "right"
    },
    "&.trend_with_icon": {
      textAlign: "right"
    },
    "&.icon": {
      textAlign: "right",
      padding: `${theme.sizes.spacing["05"]} ${theme.sizes.spacing["04"]} ${theme.sizes.spacing["05"]} ${theme.sizes.spacing["07"]}`
    }
  },
  /**
   * Table header styles
   * These styles are applied to the table header cells.
   * They include typography, background color, border, padding, and alignment.
   */
  [`& .${tableCellClasses.head}`]: {
    ...theme.typography.cellHeader,
    color: theme.colors.text.tertiary,
    height: "2.75rem",
    backgroundColor: externalHeader ? theme.colors.surface.secondary : theme.colors.surface.primary,
    borderBottom: `${theme.sizes.borderWidth["1"]} solid ${theme.colors.border.primary}`,
    padding: `${theme.sizes.spacing["06"]} ${theme.sizes.spacing["07"]}`,
    gap: theme.sizes.spacing["07"],
    "&.text": {
      textAlign: "left"
    },
    "&.value": {
      textAlign: "right"
    },
    "&.trend_with_icon": {
      textAlign: "right"
    },
    "&.icon": {
      textAlign: "right",
      padding: theme.sizes.spacing.none
    }
  }
}));
const DynamicTable = t0 => {
  const $ = c(32);
  const {
    data,
    summary,
    isLoading: t1,
    columns,
    externalHeader: t2,
    maxHeight,
    searchValue: t3,
    defaultSortBy
  } = t0;
  const isLoading = t1 === void 0 ? false : t1;
  const externalHeader = t2 === void 0 ? false : t2;
  const searchValue = t3 === void 0 ? "" : t3;
  const t4 = defaultSortBy ?? "";
  let defaultOrderByCol;
  let t5;
  let t6;
  if ($[0] !== t4) {
    const [t72, t82] = t4.split(":");
    defaultOrderByCol = t72 === void 0 ? "" : t72;
    const defaultOrder = t82 === void 0 ? "desc" : t82;
    t6 = useState;
    t5 = defaultOrder.toLowerCase();
    $[0] = t4;
    $[1] = defaultOrderByCol;
    $[2] = t5;
    $[3] = t6;
  } else {
    defaultOrderByCol = $[1];
    t5 = $[2];
    t6 = $[3];
  }
  const [order, setOrder] = t6(t5);
  const [orderBy, setOrderBy] = useState(defaultOrderByCol);
  if (!data || isLoading) {
    let t72;
    if ($[4] === /* @__PURE__ */Symbol.for("react.memo_cache_sentinel")) {
      t72 = /* @__PURE__ */jsx(DynamicTableSkeleton, {
        colCount: 5
      });
      $[4] = t72;
    } else {
      t72 = $[4];
    }
    return t72;
  }
  let t7;
  if ($[5] !== order || $[6] !== orderBy) {
    t7 = (_event, property) => {
      const isDesc = orderBy === property && order === "desc";
      setOrder(isDesc ? "asc" : "desc");
      setOrderBy(property);
    };
    $[5] = order;
    $[6] = orderBy;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const handleRequestSort = t7;
  let t8;
  if ($[8] !== columns || $[9] !== data.columns || $[10] !== handleRequestSort || $[11] !== order || $[12] !== orderBy) {
    t8 = /* @__PURE__ */jsx(MuiTableHead, {
      children: /* @__PURE__ */jsx(TableHeader, {
        columns,
        data: data.columns,
        order,
        orderBy,
        onRequestSort: handleRequestSort
      })
    });
    $[8] = columns;
    $[9] = data.columns;
    $[10] = handleRequestSort;
    $[11] = order;
    $[12] = orderBy;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== columns || $[15] !== data || $[16] !== order || $[17] !== orderBy || $[18] !== searchValue) {
    t9 = /* @__PURE__ */jsx(TableBody$1, {
      columns,
      data,
      order,
      orderBy,
      filterValue: searchValue
    });
    $[14] = columns;
    $[15] = data;
    $[16] = order;
    $[17] = orderBy;
    $[18] = searchValue;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== columns || $[21] !== searchValue || $[22] !== summary) {
    t10 = summary && summary.data && summary.data.length > 0 && searchValue.length === 0 && /* @__PURE__ */jsx(TableBody, {
      columns,
      data: summary
    });
    $[20] = columns;
    $[21] = searchValue;
    $[22] = summary;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t8 || $[26] !== t9) {
    t11 = /* @__PURE__ */jsxs(MuiTable, {
      stickyHeader: true,
      children: [t8, t9, t10]
    });
    $[24] = t10;
    $[25] = t8;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== externalHeader || $[29] !== maxHeight || $[30] !== t11) {
    t12 = /* @__PURE__ */jsx(TableContainer, {
      externalHeader,
      maxHeight,
      children: t11
    });
    $[28] = externalHeader;
    $[29] = maxHeight;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
};
export { DynamicTable as default };